// Utilities to enhance already-built Highcharts options objects (post-mapping)
// without modifying individual mapper implementations.

// Safely get numeric value from a Highcharts point (number or { y: number })
function pointToNumber(p) {
  if (p === null || p === undefined) return null;
  if (typeof p === 'number') return Number.isFinite(p) ? p : null;
  if (typeof p === 'object' && p !== null) {
    const y = p.y;
    return (y === null || y === undefined) ? null : (Number.isFinite(y) ? y : null);
  }
  return null;
}

// A point option object built for an injured player carries `custom.injured`. Its value
// never enters a group value, so it reads as null here.
function pointIsInjured(p) {
  return p !== null && typeof p === 'object' && p.custom?.injured === true;
}

function computePrimaryValues(options) {
  const series = Array.isArray(options?.series) ? options.series : [];
  const columnSeries = series.filter(s => (s?.type || 'column') === 'column');
  if (columnSeries.length === 0) return [];

  const isStacked = options?.plotOptions?.column?.stacking === 'normal' || columnSeries.some(s => s.stack);
  if (!isStacked) {
    // First column series is considered primary
    const data = columnSeries[0]?.data || [];
    return data.map(p => (pointIsInjured(p) ? null : pointToNumber(p)));
  }

  // Stacked: sum across all column series for each x index
  const maxLen = Math.max(...columnSeries.map(s => (s?.data?.length || 0)));
  const totals = new Array(maxLen).fill(null).map((_, i) => {
    let sum = 0; let hasAny = false;
    for (const s of columnSeries) {
      const p = (s?.data || [])[i];
      if (pointIsInjured(p)) return null;
      const v = pointToNumber(p);
      if (v !== null) { sum += v; hasAny = true; }
    }
    return hasAny ? sum : null;
  });
  return totals;
}

function addAveragePlotline(options, values) {
  if (!Array.isArray(values) || values.length === 0) return;
  // Exclude null/undefined AND zero values from average calculation
  const valid = values.filter(v => v !== null && v !== undefined && v !== 0);
  if (valid.length === 0) return;
  const avg = Number((valid.reduce((a, b) => a + b, 0) / valid.length).toFixed(1));

  const series = Array.isArray(options?.series) ? options.series : [];
  const columnSeries = series.filter(s => (s?.type || 'column') === 'column');
  const axisIndices = new Set(columnSeries.map(s => (typeof s?.yAxis === 'number' ? s.yAxis : 0)));

  const yAxes = Array.isArray(options?.yAxis) ? options.yAxis : [options.yAxis || {}];
  axisIndices.forEach((axisIdx) => {
    const axis = yAxes[axisIdx] || yAxes[0];
    if (!axis) return;
    axis.plotLines = Array.isArray(axis.plotLines) ? axis.plotLines : [];
    // Avoid duplicate avg lines
    if (axis.plotLines.some(pl => pl?.id === 'avg' || pl?.id === 'opt-avg')) return;
    axis.plotLines.push({
      id: 'opt-avg',
      dashStyle: 'Dash',
      color: 'black',
      width: 1,
      value: avg,
      zIndex: 5,
      label: {
        text: `Avg: ${avg}`,
        align: (axis && axis.opposite) ? 'right' : 'left',
        x: (axis && axis.opposite) ? 20 : -26
      }
    });
  });
}

function addTrendlineSeries(options, values) {
  if (!Array.isArray(values) || values.length < 2) return;
  const indexedValid = values.map((y, i) => (y === null ? null : [i, y])).filter(Boolean);
  if (indexedValid.length < 2) return;
  const n = indexedValid.length;
  const sumX = indexedValid.reduce((s, [x]) => s + x, 0);
  const sumY = indexedValid.reduce((s, [, y]) => s + y, 0);
  const sumXY = indexedValid.reduce((s, [x, y]) => s + x * y, 0);
  const sumXX = indexedValid.reduce((s, [x]) => s + x * x, 0);
  const denom = n * sumXX - sumX * sumX;
  const b = denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
  const a = (sumY - b * sumX) / n;
  const firstIdx = indexedValid[0][0];
  const lastIdx = indexedValid[indexedValid.length - 1][0];
  const trendlineData = values.map((_, i) => (i >= firstIdx && i <= lastIdx ? Number((a + b * i).toFixed(3)) : null));

  // Avoid duplicates
  const hasTrend = (options?.series || []).some(s => s?.name === 'Trend' || s?.id === 'opt-trend');
  if (hasTrend) return;
  options.series = Array.isArray(options.series) ? options.series : [];
  options.series.push({
    id: 'opt-trend',
    name: 'Trend',
    type: 'spline',
    yAxis: 0,
    data: trendlineData,
    color: 'rgba(0,0,0,0.6)',
    dashStyle: 'ShortDash',
    marker: { enabled: false },
    enableMouseTracking: false,
    showInLegend: true
  });
}

export function enhanceOptionsWithOptionalFeatures(options, { showAvgPlotline = false, showTrendlines = false } = {}) {
  if (!options || typeof options !== 'object') return options;
  const values = computePrimaryValues(options);
  // If caller requests avg OR an existing avg line is present, normalize it using non-zero values
  const yAxes = Array.isArray(options?.yAxis) ? options.yAxis : [options.yAxis || {}];
  const hasExistingAvg = yAxes.some(ax => Array.isArray(ax?.plotLines) && ax.plotLines.some(pl => pl?.id === 'avg' || pl?.id === 'opt-avg'));
  if (showAvgPlotline || hasExistingAvg) {
    // Remove existing avg and re-add using filtered values
    yAxes.forEach((ax) => {
      if (Array.isArray(ax?.plotLines)) {
        ax.plotLines = ax.plotLines.filter(pl => pl?.id !== 'avg' && pl?.id !== 'opt-avg');
      }
    });
    addAveragePlotline(options, values);
  }
  if (showTrendlines) addTrendlineSeries(options, values);
  return options;
}
